import {
  createFileRoute,
  isRedirect,
  notFound,
  Outlet,
  redirect,
} from "@tanstack/react-router"
import { GatedSessionDetailsFailReason } from "ipctg-inflow-types/InFlowEnums"

import api from "@/utilities/api"

import { cleanEmptyParams } from "@/utilities/etc"
import { ExpectedError } from "@/utilities/expectedErrors"
import { NOT_FOUND_SESSION } from "@/utilities/globals"
import { gatedSessionDetailsQueryOptions } from "@/utilities/hooks/queries/gated"
import { log } from "@/utilities/logging"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/register/$sessionID"
)({
  validateSearch: (
    search: Record<string, unknown>
  ): { reregister?: boolean | undefined; express: boolean | undefined } => {
    return cleanEmptyParams({
      reregister: search.reregister === "true" || undefined,
      express: search.express === "true" || undefined,
    })
  },
  beforeLoad: async ({ context, params, search, matches }) => {
    if (
      matches.some(
        (m) =>
          m.routeId === "/qr/$qrLookupID/gated/register/$sessionID/complete" ||
          m.routeId === "/qr/$qrLookupID/gated/register/$sessionID/confirmation"
      )
    ) {
      return
    }

    const shouldRefetchWelcome = matches.some(
      (m) =>
        m.routeId ===
          "/qr/$qrLookupID/gated/register/$sessionID/confirmation" ||
        m.routeId === "/qr/$qrLookupID/gated/register/$sessionID/express"
    )

    if (shouldRefetchWelcome) {
      context.queryClient.clear()
      await api.welcome.POST({ qr: params.qrLookupID })
    }

    try {
      const rsp = await context.queryClient.fetchQuery(
        gatedSessionDetailsQueryOptions({ sessionID: params.sessionID })
      )

      if (!search.reregister && rsp.session.isRegistered) {
        throw redirect({
          to: "/qr/$qrLookupID/gated/register/$sessionID/complete",
          params,
          search: {
            express: matches.some(
              (m) =>
                m.routeId ===
                "/qr/$qrLookupID/gated/register/$sessionID/express"
            ),
          },
        })
      }
    } catch (error) {
      if (
        error instanceof ExpectedError &&
        error.failReason === GatedSessionDetailsFailReason.SessionNotFound
      ) {
        log({
          level: "warn",
          flow: "gated:register",
          scope: "guard.session",
          message: "Session not found, showing not-found page",
          meta: { sessionID: params.sessionID },
        })
        throw notFound({
          data: {
            type: NOT_FOUND_SESSION,
          },
        })
      }
      // Redirects (e.g. already-registered users) are thrown to reroute the
      // navigation, so they land in this catch too. Only log genuine failures.
      if (!isRedirect(error)) {
        log({
          level: "error",
          flow: "gated:register",
          scope: "guard.session",
          message: "Failed to load session details",
          meta: { sessionID: params.sessionID, error },
        })
      }
      throw error
    }
  },
  shouldReload: true,
  component: Outlet,
})
